import type { MessageSummary } from "@/api/mailApi";

// Conversation grouping for the message list ("Conversation view").
//
// Groups the messages of the CURRENT PAGE only — paging stays exact (the
// pager counts messages, as the server does) and a thread whose older
// messages sit on the next page shows those there too. The reading pane
// fetches the full conversation (all folders) separately.
//
// Key per message:
//   1. the server's header-chain key (Message-ID / In-Reply-To / References,
//      POST /api/threads/keys) when available;
//   2. otherwise normalised subject, split into clusters no more than 30 days
//      apart. Generic subjects ("", "(no subject)", "Hi") also require the same
//      sender so unrelated mail doesn't collapse together. Summaries carry only
//      the sender, so "participants" is approximated that way.

export interface Conversation {
    key: string;
    /** The row shown in the list — the newest message in the page. */
    latest: MessageSummary;
    /** Newest first. */
    messages: MessageSummary[];
    /** Sender display names, oldest first, de-duplicated. */
    participants: string[];
    unread: boolean;
    flagged: boolean;
    hasAttachments: boolean;
}

const PREFIX = /^\s*(re|fwd?|aw|sv|wg|antw)(\[\d+\])?\s*:\s*/i;
const WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

export function normalizeSubject(subject: string): string {
    let s = subject.trim();
    for (;;) {
        const m = PREFIX.exec(s);
        if (!m) break;
        s = s.slice(m[0].length);
    }
    return s.toLowerCase().replace(/\s+/g, " ").trim();
}

function isGeneric(norm: string): boolean {
    return norm.length < 4 || norm === "(no subject)" || norm === "no subject";
}

export function groupConversations(
    messages: MessageSummary[],
    serverKeys?: Record<string, string>
): Conversation[] {
    const byKey = new Map<string, MessageSummary[]>();
    const order: string[] = [];
    const clusters = new Map<string, Array<{ key: string; oldest: number; newest: number }>>();
    let seq = 0;

    for (const m of messages) {
        let key: string;
        const server = serverKeys?.[m.id];
        if (server) {
            key = `h:${server}`;
        } else {
            const norm = normalizeSubject(m.subject);
            const base = isGeneric(norm) ? `g:${norm}|${m.from.email.toLowerCase()}` : `s:${norm}`;
            const at = Date.parse(m.receivedAt) || 0;
            const list = clusters.get(base) ?? [];
            let hit = list.find((c) => at >= c.oldest - WINDOW_MS && at <= c.newest + WINDOW_MS);
            if (!hit) {
                hit = { key: `${base}#${seq++}`, oldest: at, newest: at };
                list.push(hit);
                clusters.set(base, list);
            } else {
                hit.oldest = Math.min(hit.oldest, at);
                hit.newest = Math.max(hit.newest, at);
            }
            key = hit.key;
        }
        const bucket = byKey.get(key);
        if (bucket) bucket.push(m);
        else {
            byKey.set(key, [m]);
            order.push(key);
        }
    }

    return order.map((key) => {
        const list = byKey.get(key)!;
        const newestFirst = [...list].sort((a, b) => Date.parse(b.receivedAt) - Date.parse(a.receivedAt));
        const participants: string[] = [];
        for (const m of [...newestFirst].reverse()) {
            const name = (m.from.name || m.from.email).trim();
            if (name && !participants.includes(name)) participants.push(name);
        }
        return {
            key,
            // The first row of the group in list order — respects pinning.
            latest: list[0]!,
            messages: newestFirst,
            participants,
            unread: list.some((m) => !m.isRead),
            flagged: list.some((m) => m.isFlagged),
            hasAttachments: list.some((m) => m.hasAttachments)
        };
    });
}

/** "Alice, Bob, Carol" — first names once there's more than one. */
export function participantLabel(names: string[]): string {
    if (names.length <= 1) return names[0] ?? "";
    const firsts = names.map((n) => (n.includes("@") ? n.split("@")[0]! : n.split(/\s+/)[0]!));
    if (firsts.length <= 3) return firsts.join(", ");
    return `${firsts[0]} .. ${firsts.slice(-2).join(", ")}`;
}
